/**
 * Lightweight runtime browser-environment checks.
 *
 * In-app browsers (the webviews inside Gmail, Substack, Facebook, Instagram,
 * etc.) frequently block popup OAuth — `signInWithPopup` silently fails there.
 * Since SWTPA's traffic arrives from email links opened on phones, that's the
 * leading suspect for the trial-signup drop-off. UA sniffing is best-effort, not
 * exhaustive — use it to *guide* (warn / prefer email), never as a hard gate.
 */
import { getFirebaseConfig } from '@/utils/firebase/environment';

export function isInAppBrowser(ua: string = typeof navigator !== 'undefined' ? navigator.userAgent : ''): boolean {
  if (!ua) return false;
  // Known in-app webview signatures.
  const inApp = /(FBAN|FBAV|FB_IAB|Instagram|Line|Twitter|WhatsApp|Snapchat|Pinterest|; wv\)|GSA\/)/i.test(ua);
  if (inApp) return true;
  // iOS webview heuristic: iOS device but NOT mobile Safari and NOT a known
  // full browser (Chrome/Firefox/Edge use CriOS/FxiOS/EdgiOS on iOS).
  const isIOS = /iPhone|iPad|iPod/i.test(ua);
  if (isIOS) {
    const isRealBrowser = /Safari/i.test(ua) || /(CriOS|FxiOS|EdgiOS)/i.test(ua);
    if (!isRealBrowser) return true;
  }
  return false;
}

/**
 * True for any phone/tablet browser — including *regular* mobile Safari and
 * mobile Chrome, not just in-app webviews.
 *
 * `signInWithPopup` is unreliable across mobile browsers generally (popups are
 * heavily restricted / silently dismissed on iOS Safari and Android Chrome),
 * which surfaced as "Sign-in cancelled" (popup-closed) on real devices. This is
 * broader than `isInAppBrowser` on purpose: it's the popup-vs-redirect signal.
 */
export function isMobileBrowser(ua: string = typeof navigator !== 'undefined' ? navigator.userAgent : ''): boolean {
  if (!ua) return false;
  return /iPhone|iPad|iPod|Android/i.test(ua);
}

/**
 * True when the configured Firebase `authDomain` is our OWN domain rather than a
 * Firebase default (`*.firebaseapp.com` / `*.web.app`).
 *
 * This is coupled to the popup-vs-redirect choice: `signInWithPopup` only works
 * when the auth handler is served from a Firebase default authDomain. With a
 * custom (reverse-proxied) authDomain the popup completes OAuth but CANNOT post
 * its result back through the proxy → it auto-closes → `auth/popup-closed-by-
 * user`. So a custom authDomain REQUIRES redirect for everyone, desktop included.
 *
 * Reading the resolved config (not a hardcoded value) makes this adapt to
 * whatever authDomain is actually deployed — zero manual code/env coordination.
 */
export function isCustomAuthDomain(authDomain?: string): boolean {
  const domain = (authDomain ?? getFirebaseConfig().authDomain ?? '').toString().toLowerCase();
  if (!domain) return false;
  // Firebase's default auth-handler hosts — popup works ON these.
  return !(domain.endsWith('.firebaseapp.com') || domain.endsWith('.web.app'));
}

/**
 * The popup-vs-redirect decision for social OAuth. Redirect UNLESS we're on
 * desktop AND the authDomain is a Firebase default (the only combination where
 * `signInWithPopup` is reliable).
 *
 * Redirect when:
 *  - in-app webview (Gmail/Instagram/… — popups silently blocked), OR
 *  - any mobile browser (mobile Safari/Chrome handle popups unreliably →
 *    "Sign-in cancelled"), OR
 *  - the authDomain is CUSTOM (reverse-proxied — popup can't post back, breaks
 *    on desktop too).
 *
 * This makes the two mismatch-proof: flip the deployed authDomain and the client
 * follows automatically with no code change.
 */
export function shouldUseRedirect(
  ua: string = typeof navigator !== 'undefined' ? navigator.userAgent : '',
  authDomain?: string
): boolean {
  return isInAppBrowser(ua) || isMobileBrowser(ua) || isCustomAuthDomain(authDomain);
}

/**
 * A touch iPhone/iPad. iPadOS 13+ Safari reports a Mac UA, so a "Macintosh" with
 * touch points counts too; a desktop Mac has none.
 */
export function isMobileIOS(
  ua: string = typeof navigator !== 'undefined' ? navigator.userAgent : '',
  touchPoints: number = typeof navigator !== 'undefined' ? navigator.maxTouchPoints ?? 0 : 0
): boolean {
  if (touchPoints < 1) return false;
  return /iPhone|iPad|iPod/i.test(ua) || (/Macintosh/i.test(ua) && touchPoints > 1);
}
